import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { authService } from "@/services/authService";
import { Alert, Button, PasswordInput, Input } from "@/components/ui";
import { AuthShell } from "@/components/common";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { createLoginSchema, type LoginFormData } from "./loginSchema";
import {
  Mail,
  Lock,
  Shield,
  Store,
  Home,
  User,
  BadgePercent,
} from "lucide-react";
import { IubendaLink } from "@/components/legal/IubendaLink";
import { IubendaCookiePreferencesLink } from "@/components/legal/IubendaCookiePreferencesLink";
import { isApiClientError } from "@/lib/api";
import {
  configuredTestLoginProfiles,
  type TestLoginProfile,
  type TestLoginRoleId,
} from "@/lib/testLoginProfiles";
import { cn } from "@/lib/utils";

const TEST_ROLE_I18N_KEY = {
  salesAgent: "testLoginRoleSalesAgent",
  admin: "testLoginRoleAdmin",
  seller: "testLoginRoleSeller",
  host: "testLoginRoleHost",
  customer: "testLoginRoleCustomer",
} as const satisfies Record<TestLoginRoleId, string>;

const LEGAL_LINK_CLASS = "font-medium text-surface-500 hover:text-surface-900";

const VARIANT_CLASS: Record<TestLoginProfile["variant"], string> = {
  violet: "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100",
  primary:
    "border-primary-100 bg-primary-50 text-primary-700 hover:bg-primary-100",
  accent: "border-accent-100 bg-accent-50 text-accent-700 hover:bg-accent-100",
  emerald:
    "border-success-100 bg-success-50 text-success-700 hover:bg-success-100",
  sky: "border-sky-100 bg-sky-50 text-sky-700 hover:bg-sky-100",
};

function TestLoginRoleIcon({ roleId }: { roleId: TestLoginRoleId }) {
  const className = "h-4 w-4 shrink-0";
  switch (roleId) {
    case "salesAgent":
      return <BadgePercent className={className} />;
    case "admin":
      return <Shield className={className} />;
    case "seller":
      return <Store className={className} />;
    case "host":
      return <Home className={className} />;
    case "customer":
      return <User className={className} />;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);
  const resolver = useMemo(() => zodResolver(loginSchema), [loginSchema]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver,
  });

  const emailWatch = watch("email");
  const passwordWatch = watch("password");
  useEffect(() => {
    setFormError(null);
  }, [emailWatch, passwordWatch]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setFormError(null);
      setIsLoading(true);
      const session = await authService.login(data);
      login(session.access_token);
      toast.success(t("loginSuccess"));
      navigate("/");
    } catch (err) {
      const apiMsg = getApiErrorMessage(err, "loginFailed");
      let message = apiMsg;
      if (isApiClientError(err) && err.response?.status === 401) {
        const lower = apiMsg.toLowerCase();
        if (
          lower.includes("invalid credential") ||
          lower.includes("invalid kiosk") ||
          lower.includes("incorrect")
        ) {
          message = t("invalidLogin");
        }
      } else {
        const lower = apiMsg.toLowerCase();
        if (lower.includes("account disabled")) {
          message = t("accountDisabled");
        } else if (lower.includes("kiosk has been revoked")) {
          message = t("kioskRevoked");
        } else if (lower.includes("software_id")) {
          message = t("softwareIdRequired");
        }
      }
      setFormError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fillCredentials = (email: string, password: string) => {
    setValue("email", email, { shouldValidate: true });
    setValue("password", password, { shouldValidate: true });
  };

  return (
    <AuthShell
      footer={
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <IubendaLink policy="privacy" className={LEGAL_LINK_CLASS}>
              {tCommon("privacyPolicy")}
            </IubendaLink>
            <IubendaLink policy="cookie" className={LEGAL_LINK_CLASS}>
              {tCommon("cookiePolicy")}
            </IubendaLink>
            <IubendaCookiePreferencesLink className={LEGAL_LINK_CLASS}>
              {tCommon("cookiePreferences")}
            </IubendaCookiePreferencesLink>
          </div>
          <p>{tCommon("copyright", { year: new Date().getFullYear() })}</p>
        </div>
      }
    >
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-surface-900">
          {t("welcomeBack")}
        </h1>
        <p className="mt-1.5 text-sm text-surface-500">{t("signInSubtitle")}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register("email")}
          type="email"
          label={t("email")}
          placeholder={t("emailPlaceholder")}
          error={errors.email?.message}
          autoComplete="email"
          leftIcon={<Mail className="h-4 w-4" />}
        />

        <div>
          <PasswordInput
            id="seller-login-password"
            {...register("password")}
            label={t("password")}
            placeholder={t("passwordPlaceholder")}
            error={errors.password?.message}
            autoComplete="current-password"
            leftIcon={<Lock className="h-4 w-4" />}
            showPasswordLabel={t("showPassword")}
            hidePasswordLabel={t("hidePassword")}
          />
          <div className="mt-2 text-right">
            <Link
              to="/forgot-password"
              className="text-[13px] font-medium text-primary-600 hover:text-primary-700"
            >
              {t("forgotPasswordLink")}
            </Link>
          </div>
        </div>

        {formError ? <Alert variant="error">{formError}</Alert> : null}

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          {t("signIn")}
        </Button>
      </form>

      {configuredTestLoginProfiles.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-3 text-xs text-surface-400">
            <span className="h-px flex-1 bg-surface-200" />
            {t("testCredentialsTitle")}
            <span className="h-px flex-1 bg-surface-200" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {configuredTestLoginProfiles.map((profile) => (
              <button
                key={profile.roleId}
                type="button"
                onClick={() => fillCredentials(profile.email, profile.password)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-[13px] font-medium transition-colors",
                  VARIANT_CLASS[profile.variant],
                  profile.fullWidth && "col-span-2",
                )}
              >
                <TestLoginRoleIcon roleId={profile.roleId} />
                {t(TEST_ROLE_I18N_KEY[profile.roleId])}
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthShell>
  );
}
